/**
 * IndexedDB Storage Adapter for Zustand
 * 
 * 重要：このアダプタは既存のlocalStorageデータを
 * 自動的にIndexedDBにマイグレーションします。
 * データ消失を防ぐため、慎重に実装されています。
 */

// Browser API check helpers
const isBrowser = typeof globalThis !== 'undefined' && 
                  typeof (globalThis as any).window !== 'undefined' &&
                  typeof (globalThis as any).indexedDB !== 'undefined' &&
                  typeof (globalThis as any).localStorage !== 'undefined'

// Zustand PersistStorage interface
// ZustandのStorageValueは { state: T, version?: number } 形式
type StorageValue<T> = {
  state: T
  version?: number
}

interface StateStorage {
  getItem: (name: string) => StorageValue<any> | null | Promise<StorageValue<any> | null>
  setItem: (name: string, value: StorageValue<any>) => void | Promise<void>
  removeItem: (name: string) => void | Promise<void>
}

interface MigrationLog {
  storeName: string
  timestamp: string
  success: boolean
  dataSize?: number
  error?: string
}

class IndexedDBStorageAdapter {
  private dbName = 'CreaNizerDB'
  private version = 4 // v4に統一（indexeddb.tsと同じバージョン）
  private storeName = 'zustandStores'
  private db: any | null = null
  private migrationLogs: MigrationLog[] = []
  private migrationAttempted: Set<string> = new Set() // 移行試行済みを記録

  /**
   * IndexedDBを初期化
   */
  private async initDB(): Promise<any> {
    if (this.db) return this.db

    if (!isBrowser) {
      throw new Error('IndexedDB is not available in this environment')
    }

    return new Promise((resolve, reject) => {
      const request = (globalThis as any).indexedDB.open(this.dbName, this.version)

      request.onerror = () => {
        console.error('IndexedDB open error:', request.error)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as any).result
        const oldVersion = event.oldVersion
        
        console.log(`Upgrading IndexedDB from version ${oldVersion} to ${this.version}`)
        
        // 既存のv3ストアを保持（絶対に削除しない）
        // カスタム値ストア（v3で存在）
        if (!db.objectStoreNames.contains('customValues')) {
          const customValuesStore = db.createObjectStore('customValues', { keyPath: 'id' })
          customValuesStore.createIndex('type', 'type', { unique: false })
          customValuesStore.createIndex('nameType', 'nameType', { unique: false })
          customValuesStore.createIndex('usageCount', 'usageCount', { unique: false })
        }

        // プロジェクトストア（v3で存在）
        if (!db.objectStoreNames.contains('projects')) {
          const projectsStore = db.createObjectStore('projects', { keyPath: 'id' })
          projectsStore.createIndex('updatedAt', 'updatedAt', { unique: false })
          projectsStore.createIndex('name', 'name', { unique: false })
        }

        // 設定ストア（v3で存在）
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', { keyPath: 'key' })
        }

        // APIキーストア（v3で存在）
        if (!db.objectStoreNames.contains('apiKeys')) {
          const apiKeysStore = db.createObjectStore('apiKeys', { keyPath: 'id' })
          apiKeysStore.createIndex('isActive', 'isActive', { unique: false })
          apiKeysStore.createIndex('createdAt', 'createdAt', { unique: false })
        }

        // 画像プロンプトストア（v3で追加）
        if (!db.objectStoreNames.contains('imagePrompts')) {
          const imagePromptsStore = db.createObjectStore('imagePrompts', { keyPath: 'id' })
          imagePromptsStore.createIndex('createdAt', 'createdAt', { unique: false })
          imagePromptsStore.createIndex('title', 'title', { unique: false })
        }
        
        // v4で追加: zustandStoresストア
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'key' })
          console.log('Created zustandStores object store')
        }

        // v4で追加: マイグレーションログストア
        if (!db.objectStoreNames.contains('migrationLogs')) {
          const logStore = db.createObjectStore('migrationLogs', { 
            keyPath: 'id', 
            autoIncrement: true 
          })
          logStore.createIndex('storeName', 'storeName', { unique: false })
          logStore.createIndex('timestamp', 'timestamp', { unique: false })
          console.log('Created migrationLogs object store')
        }
      }
    })
  }

  /**
   * localStorageからIndexedDBへのマイグレーション
   */
  private async migrateFromLocalStorage(key: string): Promise<boolean> {
    if (!isBrowser) return false
    
    try {
      // localStorageからデータを取得
      const localData = (globalThis as any).localStorage.getItem(key)
      if (!localData) {
        console.log(`No localStorage data found for key: ${key}`)
        return false
      }

      console.log(`Found localStorage data for ${key}, migrating to IndexedDB...`)
      
      // データをパース
      const parsedData = JSON.parse(localData)
      
      // IndexedDBに保存
      const db = await this.initDB()
      const transaction = db.transaction([this.storeName], 'readwrite')
      const store = transaction.objectStore(this.storeName)
      
      await new Promise<void>((resolve, reject) => {
        const request = store.put({
          key,
          value: parsedData,
          migratedAt: new Date().toISOString(),
          originalSource: 'localStorage'
        })
        
        request.onsuccess = () => {
          console.log(`Successfully migrated ${key} to IndexedDB`)
          
          // マイグレーションログを記録
          this.logMigration({
            storeName: key,
            timestamp: new Date().toISOString(),
            success: true,
            dataSize: localData.length
          })
          
          // バックアップとして元のlocalStorageデータを別キーで保存
          if (isBrowser) (globalThis as any).localStorage.setItem(`${key}_backup_${Date.now()}`, localData)
          
          // 元のlocalStorageデータを削除
          if (isBrowser) (globalThis as any).localStorage.removeItem(key)
          
          resolve()
        }
        
        request.onerror = () => {
          console.error(`Failed to migrate ${key}:`, request.error)
          reject(request.error)
        }
      })
      
      return true
    } catch (error) {
      console.error(`Migration error for ${key}:`, error)
      
      // マイグレーションログを記録
      this.logMigration({
        storeName: key,
        timestamp: new Date().toISOString(),
        success: false,
        error: String(error)
      })
      
      return false
    }
  }

  /**
   * マイグレーションログを記録
   */
  private async logMigration(log: MigrationLog): Promise<void> {
    try {
      const db = await this.initDB()
      const transaction = db.transaction(['migrationLogs'], 'readwrite')
      const store = transaction.objectStore('migrationLogs')
      store.add(log)
      this.migrationLogs.push(log)
    } catch (error) {
      console.error('Failed to log migration:', error)
    }
  }

  /**
   * Zustand StateStorage実装を作成
   */
  createStorage(storageKey: string): StateStorage {
    return {
      getItem: async (name: string): Promise<StorageValue<any> | null> => {
        const maxRetries = 3
        let lastError: any = null
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            // IndexedDBからデータを取得
            const db = await this.initDB()
            const transaction = db.transaction([this.storeName], 'readonly')
            const store = transaction.objectStore(this.storeName)
            
            const indexedDBData = await new Promise<any>((resolve, reject) => {
              const request = store.get(name)
              
              request.onsuccess = () => {
                const result = request.result
                if (result && result.value) {
                  resolve(result.value)
                } else {
                  resolve(null)
                }
              }
              
              request.onerror = () => {
                console.error(`Failed to get ${name} from IndexedDB (attempt ${attempt}/${maxRetries}):`, request.error)
                reject(request.error)
              }
            })
            
            // IndexedDBにデータがある場合はそれを返す
            if (indexedDBData) {
              return indexedDBData
            }
            
            // データがない場合はnullを返す（これは正常な状態）
            return null
            
          } catch (error) {
            lastError = error
            console.error(`Error in getItem for ${name} (attempt ${attempt}/${maxRetries}):`, error)
            
            // 最後の試行でない場合は少し待ってリトライ
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 100 * attempt))
              continue
            }
          }
        }
        
        // すべてのリトライが失敗した場合
        console.error(`All retries failed for ${name}. Attempting recovery...`, lastError)
        
        // 緊急フォールバック：localStorageから一時的に復元を試みる
        if (isBrowser) {
          try {
            // バックアップからの復元を試みる
            const backupKeys = []
            for (let i = 0; i < (globalThis as any).localStorage.length; i++) {
              const key = (globalThis as any).localStorage.key(i)
              if (key && key.startsWith(`${name}_backup_`)) {
                backupKeys.push(key)
              }
            }
            
            if (backupKeys.length > 0) {
              // 最新のバックアップを使用
              backupKeys.sort().reverse()
              const latestBackup = (globalThis as any).localStorage.getItem(backupKeys[0])
              if (latestBackup) {
                console.warn(`Recovered ${name} from backup: ${backupKeys[0]}`)
                const recoveredData = JSON.parse(latestBackup)
                // 復元したデータをIndexedDBに保存を試みる（後で非同期で実行）
                Promise.resolve().then(async () => {
                  try {
                    const db = await this.initDB()
                    const transaction = db.transaction([this.storeName], 'readwrite')
                    const store = transaction.objectStore(this.storeName)
                    store.put({
                      key: name,
                      value: recoveredData,
                      updatedAt: new Date().toISOString()
                    })
                  } catch (error) {
                    console.error('Failed to save recovered data to IndexedDB:', error)
                  }
                })
                return recoveredData
              }
            }
          } catch (e) {
            console.error('Backup recovery failed:', e)
          }
        }
        
        // 復元も失敗した場合はnullを返す（データリセットを防ぐため）
        console.error(`CRITICAL: Could not recover data for ${name}. Returning null.`)
        return null
      },

      setItem: async (name: string, value: StorageValue<any>): Promise<void> => {
        const maxRetries = 3
        let lastError: any = null
        
        // 自動バックアップ（重要なストアのみ）
        // バックアップ無効化（容量超過対策）- 有効化する場合は下の条件に戻す
        // if (isBrowser && (name.includes('consultation') || name.includes('textgame'))) {
        if (false) {
          try {
            const timestamp = Date.now()
            const backupKeyName = `${name}_backup_${timestamp}`
            ;(globalThis as any).localStorage.setItem(backupKeyName, JSON.stringify(value))
            
            // 古いバックアップを削除（最新3つを保持）
            const existingBackupKeys: string[] = []
            for (let i = 0; i < (globalThis as any).localStorage.length; i++) {
              const key = (globalThis as any).localStorage.key(i)
              if (key && key.startsWith(`${name}_backup_`)) {
                existingBackupKeys.push(key)
              }
            }
            
            if (existingBackupKeys.length > 3) {
              existingBackupKeys.sort()
              const keysToDelete = existingBackupKeys.slice(0, existingBackupKeys.length - 3)
              keysToDelete.forEach(key => (globalThis as any).localStorage.removeItem(key))
            }
          } catch (e) {
            console.warn('Backup creation failed:', e)
          }
        }
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const db = await this.initDB()
            const transaction = db.transaction([this.storeName], 'readwrite')
            const store = transaction.objectStore(this.storeName)
            
            await new Promise<void>((resolve, reject) => {
              const request = store.put({
                key: name,
                value: value,
                updatedAt: new Date().toISOString()
              })
              
              request.onsuccess = () => {
                resolve()
              }
              
              request.onerror = () => {
                console.error(`Failed to set ${name} (attempt ${attempt}/${maxRetries}):`, request.error)
                reject(request.error)
              }
            })
            
            // 成功したら終了
            return
            
          } catch (error) {
            lastError = error
            console.error(`Error in setItem for ${name} (attempt ${attempt}/${maxRetries}):`, error)
            
            // 最後の試行でない場合は少し待ってリトライ
            if (attempt < maxRetries) {
              await new Promise(resolve => setTimeout(resolve, 100 * attempt))
              continue
            }
          }
        }
        
        // すべてのリトライが失敗した場合
        console.error(`All retries failed for setItem ${name}:`, lastError)
        throw lastError
      },

      removeItem: async (name: string): Promise<void> => {
        try {
          const db = await this.initDB()
          const transaction = db.transaction([this.storeName], 'readwrite')
          const store = transaction.objectStore(this.storeName)
          
          return new Promise((resolve, reject) => {
            const request = store.delete(name)
            
            request.onsuccess = () => {
              // localStorageの操作は削除
              resolve()
            }
            
            request.onerror = () => {
              console.error(`Failed to remove ${name}:`, request.error)
              reject(request.error)
            }
          })
        } catch (error) {
          console.error(`Error in removeItem for ${name}:`, error)
          throw error
        }
      }
    }
  }

  /**
   * マイグレーション状態を取得
   */
  async getMigrationStatus(): Promise<{
    logs: MigrationLog[]
    summary: {
      total: number
      successful: number
      failed: number
    }
  }> {
    try {
      const db = await this.initDB()
      const transaction = db.transaction(['migrationLogs'], 'readonly')
      const store = transaction.objectStore('migrationLogs')
      
      return new Promise((resolve, reject) => {
        const request = store.getAll()
        
        request.onsuccess = () => {
          const logs = request.result as MigrationLog[]
          const summary = {
            total: logs.length,
            successful: logs.filter(l => l.success).length,
            failed: logs.filter(l => !l.success).length
          }
          resolve({ logs, summary })
        }
        
        request.onerror = () => {
          reject(request.error)
        }
      })
    } catch (error) {
      console.error('Failed to get migration status:', error)
      return {
        logs: this.migrationLogs,
        summary: {
          total: this.migrationLogs.length,
          successful: this.migrationLogs.filter(l => l.success).length,
          failed: this.migrationLogs.filter(l => !l.success).length
        }
      }
    }
  }

  /**
   * すべてのlocalStorageデータをバックアップ
   */
  async backupAllLocalStorage(): Promise<void> {
    if (!isBrowser) return
    
    const timestamp = Date.now()
    const backup: Record<string, any> = {}
    
    // すべてのlocalStorageキーを取得
    for (let i = 0; i < (globalThis as any).localStorage.length; i++) {
      const key = (globalThis as any).localStorage.key(i)
      if (key && !key.includes('_backup_') && !key.includes('_fallback')) {
        backup[key] = (globalThis as any).localStorage.getItem(key)
      }
    }
    
    // バックアップをIndexedDBに保存
    try {
      const db = await this.initDB()
      const transaction = db.transaction(['migrationLogs'], 'readwrite')
      const store = transaction.objectStore('migrationLogs')
      
      await new Promise<void>((resolve, reject) => {
        const request = store.add({
          storeName: 'FULL_BACKUP',
          timestamp: new Date().toISOString(),
          success: true,
          backup: backup,
          dataSize: JSON.stringify(backup).length
        })
        
        request.onsuccess = () => {
          console.log('Full localStorage backup created')
          resolve()
        }
        
        request.onerror = () => {
          console.error('Failed to create backup:', request.error)
          // フォールバック：localStorageに保存
          if (isBrowser) (globalThis as any).localStorage.setItem(`full_backup_${timestamp}`, JSON.stringify(backup))
          resolve()
        }
      })
    } catch (error) {
      console.error('Backup error:', error)
      // フォールバック：localStorageに保存
      if (isBrowser) (globalThis as any).localStorage.setItem(`full_backup_${timestamp}`, JSON.stringify(backup))
    }
  }
}

// シングルトンインスタンスをエクスポート
export const indexedDBStorage = new IndexedDBStorageAdapter()

// 便利な関数をエクスポート
export function createIndexedDBStorage(storageKey: string): StateStorage {
  return indexedDBStorage.createStorage(storageKey)
}